import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { ServerSummary } from '@kcdash/shared';
import { StatusBadge } from '../components/StatusBadge.js';
import { CreateServerModal } from './CreateServerModal.js';
import { Server, Plus, Users, Globe, Play, Square, RotateCw } from 'lucide-react';

export const ServersListPage: React.FC = () => {
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const navigate = useNavigate();

  const loadServers = async () => {
    try {
      const data = await api.getServers();
      setServers(data);
    } catch (err) {
      console.error('Failed to load servers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutral-100">KCD Multiplayer Dedicated Servers</h2>
          <p className="text-xs text-neutral-400 mt-1 font-mono">
            Manage instances, process state and configurations on this Linux VPS
          </p>
        </div>

        <button
          onClick={() => setIsCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Create Server</span>
        </button>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-neutral-500 font-mono text-sm">
          Loading servers...
        </div>
      ) : servers.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-panel-border rounded-xl bg-panel-card/40">
          <Server className="w-12 h-12 mx-auto text-neutral-600 mb-3" />
          <h3 className="text-base font-semibold text-neutral-200">No servers configured</h3>
          <p className="text-xs text-neutral-400 mt-1 mb-4">
            Get started by creating your first Kingdom Come: Deliverance II server instance.
          </p>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="px-4 py-2 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white transition"
          >
            Create Server
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {servers.map((server) => (
            <div
              key={server.id}
              onClick={() => navigate(`/servers/${server.id}`)}
              className="bg-panel-card border border-panel-border hover:border-brand-800/80 rounded-xl p-5 cursor-pointer transition shadow-sm flex flex-col justify-between group"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="font-semibold text-neutral-100 text-sm group-hover:text-brand-400 transition truncate">
                    {server.name}
                  </div>
                  <StatusBadge state={server.state} size="sm" />
                </div>

                <p className="text-xs text-neutral-400 line-clamp-2 mb-4">
                  {server.description || 'No description provided'}
                </p>

                <div className="grid grid-cols-2 gap-2 text-xs font-mono bg-panel-subtle p-3 rounded-lg border border-panel-border/60 mb-4">
                  <div>
                    <span className="text-neutral-500 text-[10px] block uppercase">Address</span>
                    <span className="text-neutral-300 font-medium">:{server.port}</span>
                  </div>
                  <div>
                    <span className="text-neutral-500 text-[10px] block uppercase">Players</span>
                    <span className="text-neutral-300 font-medium flex items-center gap-1">
                      <Users className="w-3 h-3 text-brand-400" />
                      {server.currentPlayers} / {server.maxPlayers}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500 text-[10px] block uppercase">Gamemode</span>
                    <span className="text-neutral-300 font-medium truncate block">{server.gamemode}</span>
                  </div>
                  <div>
                    <span className="text-neutral-500 text-[10px] block uppercase">Map</span>
                    <span className="text-neutral-300 font-medium truncate block">{server.map}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-panel-border/60 text-[11px] text-neutral-500 font-mono">
                <span>{server.id}</span>
                <span className="text-brand-400 font-sans font-medium text-xs flex items-center gap-1 group-hover:translate-x-0.5 transition">
                  Manage &rarr;
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateServerModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(srv) => {
          setServers([...servers, srv]);
          navigate(`/servers/${srv.id}`);
        }}
      />
    </div>
  );
};
